import { ElementType, memo } from 'react';
import classnames from 'classnames';

import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import isValidKey from 'bloko/common/isValidKey';

import translation from 'src/components/translation';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import { HhTariff } from 'src/models/price/hhTariff';
import { PriceCartProductType } from 'src/models/price/priceCart';
import { BillingPlatform } from 'src/models/price/product.types';
import { ServiceItemCode } from 'src/models/price/productCodes';
import {
    getPublicationCode,
    isBrandingProduct,
    isBundleProduct,
    isClickmeProduct,
    isOptionProduct,
    isPfpProduct,
    isPublicationsProduct,
    isResumeAccessProduct,
    isZpEventProduct,
} from 'src/utils/price/products';

import Branding from 'src/components/Price/PriceCart/PriceCartProduct/Branding';
import Clickme from 'src/components/Price/PriceCart/PriceCartProduct/Clickme';
import DbAccess from 'src/components/Price/PriceCart/PriceCartProduct/DbAccess';
import PayForPerformance from 'src/components/Price/PriceCart/PriceCartProduct/PayForPerformance';
import Publications from 'src/components/Price/PriceCart/PriceCartProduct/Publications';
import ZpBranding from 'src/components/Price/PriceCart/PriceCartProduct/ZpBranding';
import ZpBundles from 'src/components/Price/PriceCart/PriceCartProduct/ZpBundles';
import ZpEvent from 'src/components/Price/PriceCart/PriceCartProduct/ZpEvent';
import ZpPromotion from 'src/components/Price/PriceCart/PriceCartProduct/ZpPromotion';
import ZpPublications from 'src/components/Price/PriceCart/PriceCartProduct/ZpPublications';
import ZpResumeAccess from 'src/components/Price/PriceCart/PriceCartProduct/ZpResumeAccess';

import styles from './price-cart-content.less';

interface PriceCartContentSwitcherProps {
    hideLastDivider?: boolean;
}

const TrlKeys = {
    old: {
        [ServiceItemCode.StandartPlus]: 'employer.price2.gift.title.RENEWAL_VP',
        [ServiceItemCode.Premium]: 'employer.price2.gift.title.VPREM',
    },
    new: {
        [ServiceItemCode.StandartPlus]: 'employer.price.gift.title.RENEWAL_VP',
        [ServiceItemCode.Premium]: 'employer.price.gift.title.VPREM',
    },
    free: 'Free',
};

const ProductsComponentsMap = {
    [HhTariff.CrossCivBundle]: DbAccess,
    [HhTariff.CrossCivVpplBundle]: DbAccess,
    [HhTariff.CrossCivZp]: DbAccess,
};

const PriceCartContentSwitcher: TranslatedComponent<PriceCartContentSwitcherProps> = ({
    hideLastDivider = false,
    trls,
}) => {
    const { products, gifts, translations } = useSelectorNonNullable(({ priceCart }) => priceCart);

    const getProductComponent = (product: PriceCartProductType): ElementType | null => {
        if (!product.tariff || product.seller === BillingPlatform.ZP) {
            if (isBundleProduct(product)) {
                return ZpBundles;
            }
            if (isOptionProduct(product)) {
                return ZpPromotion;
            }
            if (isZpEventProduct(product)) {
                return ZpEvent;
            }
            if (isResumeAccessProduct(product)) {
                return product.seller === BillingPlatform.ZP ? ZpResumeAccess : DbAccess;
            }
            // На прайсе все публикации — VPPL
            if (isPublicationsProduct(product)) {
                return product.seller === BillingPlatform.ZP ? ZpPublications : Publications;
            }
            if (isClickmeProduct(product)) {
                return Clickme;
            }
            if (isBrandingProduct(product)) {
                return product.seller === BillingPlatform.ZP ? ZpBranding : Branding;
            }
            if (isPfpProduct(product)) {
                return PayForPerformance;
            }
        } else if (isValidKey(ProductsComponentsMap, product.tariff)) {
            return ProductsComponentsMap[product.tariff];
        }

        return null;
    };

    const renderProduct = (product: PriceCartProductType) => {
        const Component = getProductComponent(product);

        if (Component) {
            return <Component product={product} translations={translations} />;
        }

        return null;
    };

    return (
        <ul className={classnames({ [styles.hideLastDivider]: hideLastDivider })}>
            {products.map((product) => {
                const productCode = isPublicationsProduct(product) ? getPublicationCode(product) : product.code;

                return (
                    <li key={product.itemId} className={styles.cartItem} data-qa={`cart-item ${productCode || ''}`}>
                        {renderProduct(product)}
                    </li>
                );
            })}
            {gifts.map(({ products }) => {
                const giftCode = products[0].code as keyof typeof TrlKeys.new;
                return (
                    <li key={giftCode} className={styles.cartItem} data-qa={`cart-gift ${giftCode}`}>
                        <div className={styles.giftWrapper}>
                            <span>{trls[TrlKeys.new[giftCode]]}</span>
                            <span>{trls[TrlKeys.free]}</span>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};
export default memo(translation(PriceCartContentSwitcher));
